import React, { useState } from "react";
import {
  TextFieldComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import {
  resolveNumericInputMin,
  resolveNumericInputMax,
  allowsNegativeNumericInput,
} from "../../state/utils/conditionalValidationUtils";
import { validateMaxCurrentYear } from "../../utils/yearValidation";

interface TextFieldProps {
  config: TextFieldComponent;
  value?: string;
  onChange?: (name: string, value: string) => void;
  externalError?: string;
}

export const TextField: React.FC<TextFieldProps> = ({
  config,
  value = "",
  onChange,
  externalError,
}) => {
  const [error, setError] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  const validateField = (inputValue: string): string => {
    // Check required property first (from IsMandatory)
    if (config.required && !inputValue.trim()) {
      return `${config.label || "This field"} is required`;
    }

    // Then check validation rules if they exist
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (!inputValue.trim())
            return rule.message || "This field is required";
          break;
        case ValidationRule.MIN_LENGTH:
          if (inputValue.length < (rule.value as number))
            return rule.message || `Minimum ${rule.value} characters required`;
          break;
        case ValidationRule.MAX_LENGTH:
          if (inputValue.length > (rule.value as number))
            return rule.message || `Maximum ${rule.value} characters allowed`;
          break;
        case ValidationRule.EMAIL:
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(inputValue))
            return rule.message || "Please enter a valid email address";
          break;
        case ValidationRule.PATTERN:
          const regex = new RegExp(rule.value as string);
          if (!regex.test(inputValue)) return rule.message || "Invalid format";
          break;
        case ValidationRule.MIN_VALUE: {
          const numVal = parseFloat(inputValue);
          if (!isNaN(numVal) && typeof rule.value === "number" && numVal < rule.value)
            return rule.message || `Minimum value is ${rule.value}`;
          break;
        }
        case ValidationRule.MAX_VALUE: {
          const numVal = parseFloat(inputValue);
          if (!isNaN(numVal) && typeof rule.value === "number" && numVal > rule.value)
            return rule.message || `Maximum value is ${rule.value}`;
          break;
        }
        case ValidationRule.MAX_CURRENT_YEAR: {
          const yearError = validateMaxCurrentYear(inputValue, rule.message);
          if (yearError) return yearError;
          break;
        }
      }
    }
    return "";
  };

  const inputType = getInputType();
  const controlRecord = config as Record<string, unknown>;
  const allowNegative = allowsNegativeNumericInput(controlRecord);
  const numericMin =
    inputType === "number" ? resolveNumericInputMin(controlRecord) : undefined;
  const numericMax =
    inputType === "number" ? resolveNumericInputMax(controlRecord) : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (inputType === "number") {
      if (allowNegative) {
        if (newValue !== "" && !/^-?\d*\.?\d*$/.test(newValue)) return;
      } else {
        // Only allow unsigned numeric strings (no "+", no "-", optional decimal part)
        if (newValue !== "" && !/^\d*\.?\d*$/.test(newValue)) return;
      }
      if (newValue !== "" && newValue !== "." && newValue !== "-" && newValue !== "-.") {
        const num = parseFloat(newValue);
        if (!isNaN(num)) {
          if (numericMin !== undefined && num < numericMin) return;
          if (numericMax !== undefined && num > numericMax) return;
        }
      }
    }
    const validationError = validateField(newValue);
    setError(validationError);
    onChange?.(config.name, newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputType !== "number") return;
    if (e.key === "e" || e.key === "E") {
      e.preventDefault();
      return;
    }
    if (e.key === "+") {
      e.preventDefault();
      return;
    }
    if (e.key === "-" && !allowNegative) {
      if (numericMin === undefined || numericMin >= 0) {
        e.preventDefault();
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    const validationError = validateField(value);
    setError(validationError);
  };

  function getInputType() {
    switch (config.inputType) {
      case "email":
        return "email";
      case "password":
        return "password";
      case "number":
        return "number";
      case "phone":
        return "tel";
      case "url":
        return "url";
      case "time":
        return "time";
      default:
        return "text";
    }
  }

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const inputStyles: React.CSSProperties = {
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 14, // Standardized to 14px (text-sm)
    fontWeight: config.style?.fontWeight || "normal",
    // Only set color/backgroundColor if explicitly provided AND not in dark mode, otherwise let Tailwind handle it
    // In dark mode, always use light text for visibility
    ...(config.style?.textColor && !config.disabled && !document.documentElement.classList.contains('dark') ? { color: config.style.textColor } : {}),
    ...(config.style?.backgroundColor && !config.disabled && !document.documentElement.classList.contains('dark') ? { backgroundColor: config.style.backgroundColor } : {}),
    textAlign: config.style?.textAlign || "left",
    fontFamily: "inherit", // Use system font stack
  };

  return (
    <div className={`${getWidthClass()} px-1`} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <input
        type={inputType}
        name={config.name}
        placeholder={config.placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        disabled={config.disabled}
        maxLength={config.maxLength}
        min={numericMin}
        max={numericMax}
        className={`w-full py-2 px-3 text-sm font-normal text-left border border-1 outline-none transition-colors duration-200 font-sans bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-white dark:!text-white placeholder-gray-400 dark:placeholder-gray-500 ${config.disabled ? "cursor-not-allowed opacity-70 dark:opacity-100 bg-gray-100 dark:bg-gray-700 dark:!text-white" : "cursor-text"
          } ${error
            ? "border-danger-500 dark:border-danger-500"
            : isFocused
              ? "border-primary-500 dark:border-primary-400"
              : "border-gray-300 dark:border-gray-600"
          } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
        style={inputStyles}
      />

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-xs mt-1">
          {error || externalError}
        </div>
      )}

      {config.helperText && !error && !externalError && (
        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{config.helperText}</div>
      )}
    </div>
  );
};
